import type { GetApiLabParams, LabViewDTO } from '@smartsash/api-client';
import { createListSearchSchema } from '@smartsash/data-screen';
import type { TableMeta } from '@tanstack/react-table';
import { z } from 'zod';

import { ADMIN_LIST_SEARCH_DEFAULTS, adminListShape } from '@/router/listSearch';
import { firstNonEmpty } from '@/utils/firstNonEmpty';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

/**
 * `groupId` is how Groups links into this list, and `instituteId` is how Institutes does. Both must
 * be declared or `validateSearch` would strip them: the schema, not the router, is what a validated
 * route preserves. `coerce` because the router parses a bare numeric value into a number.
 *
 * `groupId` merges into the real `groupId` query parameter (see `laboratoriesQueryParams`) rather
 * than narrowing the loaded page — `GetApiLabParams` accepts it since the filtering-foundation API
 * landed. `instituteId` is carried but unread — it was already dead before the adoption (`FT-*`),
 * and wiring it to the institution filter would be a behaviour change, not a migration.
 */
export const laboratoriesSearchSchema = createListSearchSchema({
  ...adminListShape,
  groupId: z.coerce.string().optional().catch(undefined),
  instituteId: z.coerce.string().optional().catch(undefined),
});

export type LaboratoriesSearch = z.infer<typeof laboratoriesSearchSchema>;

/**
 * The two builder fields that reach `GetApiLabParams`. Both are columns of this table,
 * re-vocabularied in the filter so they carry ids rather than the names they render.
 */
export const LABORATORIES_INSTITUTE_FIELD = 'institute';
export const LABORATORIES_GROUP_FIELD = 'group';

/**
 * Column id → the endpoint's `sortBy` value.
 *
 * `GET /api/Lab` whitelists `name` and `createdAt` only. `amountDevices` is computed per row rather
 * than stored, so the device count column cannot be ordered by the server and is not `sortable`.
 */
export const LABORATORIES_SORT_KEYS: Readonly<Record<string, string>> = { company: 'name' };

export const LABORATORIES_FILTER_PARAMS: FilterParamMap<GetApiLabParams> = {
  [LABORATORIES_INSTITUTE_FIELD]: { kind: 'multi', param: 'institutionId' },
  [LABORATORIES_GROUP_FIELD]: { kind: 'multi', param: 'groupId' },
};

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiLabParams` (HS-869 follow-up). Shared by
 * the screen's own `query` and `LaboratoriesFilter`'s cache read, which have to build the exact same
 * params or the filter panel would read a different cache entry than the table renders from.
 *
 * A link from a group's detail screen still arrives as `?groupId=`, which predates the builder and
 * its single `?filters=` carrier. It merges into the same `groupId` parameter the builder writes —
 * the link's group and any the builder holds are one OR — rather than narrowing the fetched page, so
 * the badge and the pager describe what the table actually shows.
 */
export function laboratoriesQueryParams(search: LaboratoriesSearch): GetApiLabParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '');
  const built = buildFilterParams(conditions, LABORATORIES_FILTER_PARAMS);
  const groupId =
    search.groupId === undefined
      ? built.groupId
      : [...new Set([...(built.groupId ?? []), search.groupId])];
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiLabParams>(search.sort, LABORATORIES_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...built,
    ...(groupId ? { groupId } : {}),
  };
}

/**
 * The text each column holds for a row (HS-869). `institute` and `group` take the table's `meta`:
 * `LabViewDTO` carries only the foreign keys, and the names are joined client-side. Both fall back
 * to the id, which is what the page rendered before.
 */
export const laboratoriesColumnText = {
  // P2-02: `LabViewDTO` declares no readable code, so the name is all there is to show.
  company: (row: LabViewDTO) => row.name ?? '',
  institute: (row: LabViewDTO, meta: TableMeta<LabViewDTO> | undefined) =>
    firstNonEmpty(
      row.institutionId == null ? undefined : meta?.institutionNames?.get(row.institutionId),
      row.institutionId,
    ),
  group: (row: LabViewDTO, meta: TableMeta<LabViewDTO> | undefined) =>
    firstNonEmpty(
      row.groupId == null ? undefined : meta?.groupNames?.get(row.groupId),
      row.groupId,
    ),
  devices: (row: LabViewDTO) => String(row.amountDevices ?? 0),
  // The manager columns render empty until the DTO carries one — unchanged by the adoption.
  manager: () => '',
} as const;

export type LaboratoriesColumnId = keyof typeof laboratoriesColumnText;

/**
 * The search that links an institution's "View laboratories" action to this list, scoped to that
 * institution — mirrors `devicesFilteredBy`/`usersFilteredByInstitution`.
 */
export function laboratoriesFilteredByInstitution(institutionId: string) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [
      { field: LABORATORIES_INSTITUTE_FIELD, operator: 'eq' as const, values: [institutionId] },
    ],
  };
}
